"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getHtmlItemsStrings = getHtmlItemsStrings;
exports.translateHtmlItems = translateHtmlItems;
const lodash_1 = __importDefault(require("lodash"));
const immer_1 = require("immer");
const ItemsHtmlConverter_1 = __importDefault(require("./ItemsHtmlConverter"));
/** Gets all unique strings from html items */
function getHtmlItemsStrings(items) {
    const strings = new Set();
    // Create collector function that just saves strings and returns them unchanged
    const collectStrings = (str) => {
        // Only collect non-empty strings
        if (str) {
            strings.add(str);
        }
        return str;
    };
    // Use existing translation function but with collector
    translateHtmlItems(items, collectStrings);
    return Array.from(strings);
}
/** Translates items in an html items list with expressions */
function translateHtmlItems(items, translate) {
    return (0, immer_1.produce)(items, draft => {
        function translateItems(itemList) {
            // Accumulate simple items
            let simpleItems = [];
            let simpleItemStartIndex = null;
            /** Process accumulated simple nodes which are simple inline elements */
            function processSimpleNodes() {
                if (simpleItems.length === 0) {
                    return;
                }
                // Create html string with {0}, {1}, etc. for expressions and html for other items
                const expressions = [];
                const converter = new TranslationItemsHtmlConverter(item => {
                    if (item.type === "expr") {
                        const expr = item;
                        if (expr.labelText) {
                            expr.labelText = translate(expr.labelText);
                        }
                        expressions.push(expr);
                        return `{${expressions.length - 1}}`;
                    }
                    return "";
                });
                // Convert items to html which has the side effect of translating expressions
                let html = converter.convertItemsToHtml(simpleItems);
                // If there was no text, do nothing except translate expressions
                if (!simpleItems.some(doesItemContainText)) {
                    return;
                }
                // Remove leading and trailing whitespace from the HTML
                const leftWhitespace = html.slice(0, html.length - html.trimStart().length);
                const rightWhitespace = html.slice(html.trimEnd().length);
                html = html.trim();
                if (!html) {
                    return;
                }
                let translatedHtml = leftWhitespace + translate(html) + rightWhitespace;
                // Parse translatedHtml into items by first replacing {0}, {1}, etc. with html that can be converted to items
                for (let i = 0; i < expressions.length; i++) {
                    const expr = expressions[i];
                    translatedHtml = translatedHtml.replace(new RegExp(`\\{${i}\\}`, "g"), `<span data-embed="${lodash_1.default.escape(JSON.stringify(expr))}"></span>`);
                }
                // Wrap in a div to prevent whitespace from being trimmed.
                const translatedItems = (converter.convertElemToItems(new DOMParser().parseFromString("<div>" + translatedHtml + "</div>", "text/html").body.firstChild));
                // Replace the simple items with the translated items
                itemList.splice(simpleItemStartIndex, simpleItems.length, ...translatedItems);
                simpleItems = [];
                simpleItemStartIndex = null;
            }
            for (let i = 0; i < itemList.length; i++) {
                const item = itemList[i];
                // If the child is a simple node, add it to the list
                if (isItemSimple(item)) {
                    simpleItems.push(item);
                    if (simpleItemStartIndex == null) {
                        simpleItemStartIndex = i;
                    }
                }
                else {
                    // If we have gathered simple nodes, we need to return/replace them
                    processSimpleNodes();
                    simpleItemStartIndex = null;
                    simpleItems = [];
                    // Handle the current node
                    if (typeof item !== "string" && item.type === "element" && item.items) {
                        translateItems(item.items);
                    }
                }
            }
            if (simpleItems.length > 0) {
                processSimpleNodes();
                simpleItemStartIndex = null;
                simpleItems = [];
            }
        }
        translateItems(draft);
    });
}
/** ItemsHtmlConverter that has a callback for handling special items */
class TranslationItemsHtmlConverter extends ItemsHtmlConverter_1.default {
    handleSpecialItem;
    constructor(handleSpecialItem) {
        super();
        this.handleSpecialItem = handleSpecialItem;
    }
    convertSpecialItemToHtml(item) {
        return this.handleSpecialItem(item);
    }
}
/**
 * Check if an html item is simple.
 * A node is simple if it is:
 *  - a string
 *  - a tag that is a "b", "i", "u", "a", "strong", "em" tag with no children or only simple children
 * Only sequences of simple nodes are exported to translation entries as HTML.
 */
function isItemSimple(node) {
    if (typeof node === "string") {
        return true;
    }
    if (node.type === "expr") {
        return true;
    }
    if (node.type === "element") {
        const element = node;
        if (["b", "i", "u", "a", "strong", "em"].indexOf(element.tag) === -1) {
            return false;
        }
        return (element.items || []).every(child => isItemSimple(child));
    }
    return false;
}
/** Determines if an item contains any non-empty text strings */
function doesItemContainText(item) {
    if (typeof item === "string") {
        return item.trim().length > 0;
    }
    if (item.type === "element") {
        return (item.items || []).some(child => doesItemContainText(child));
    }
    return false;
}
